/* eslint-disable react/jsx-no-bind, react/no-unstable-nested-components, react/jsx-newline, react/jsx-one-expression-per-line, react/no-multi-comp */
import { Badge, Button } from "react-bootstrap";
import {
	CalendarIcon,
	ClockIcon,
	ExclamationCircleIcon,
	GlobeIcon,
	MapIcon,
	PaperClipIcon,
	SupportIcon,
	TicketIcon,
	UserCircleIcon
} from "@heroicons/react/outline";
import { format, utcToZonedTime } from "date-fns-tz";
import { useCallback, useMemo } from "react";
import PropTypes from "prop-types";

import CourseFacilitators from "./CourseFacilitators";
import CourseSidebarItem from "./CourseSidebarItem";

import { googleMapsLink, isInPersonEvent } from "../../../helpers/event";
import { getTimezone } from "../../../helpers/dates";

const timeZone = getTimezone();

const CourseSidebar = ({ course }) => {
	const inPersonClass = useMemo(() => isInPersonEvent(course), [course.id]);
	const renderLocation = useCallback(() => {
		const { location } = course;

		if (!location) {
			return null;
		}

		const { address } = location;

		return (
			<>
				<address>
					{location.name}
					{inPersonClass ? (
						<>
							<br />
							{!!address.addressline1 && <>{address.addressline1}<br /></>}
							{!!address.addressline2 && <>{address.addressline2}<br /></>}
							{address.city}, {address.state} {address.zip}
						</>
					) : null}
				</address>
				{inPersonClass ? (
					<Button
						className="p-0 mb-2"
						href={googleMapsLink(course)}
						target="_blank"
						variant="link"
					>
						View Map &gt;
					</Button>) : null}
			</>
		);
	}, [course.id, inPersonClass]);

	return (
		<aside className="py-4 px-2">
			{!!course.facilitator && (
				<CourseSidebarItem
					Icon={UserCircleIcon}
					description={<CourseFacilitators facilitators={course.facilitator} />}
					title="Facilitator"
				/>
			)}

			<CourseSidebarItem
				Icon={CalendarIcon}
				description={format(utcToZonedTime(course.date, timeZone), "cccc, LLLL do")}
				title="Date"
			/>

			<CourseSidebarItem
				Icon={ClockIcon}
				description={() => (
					<p>
						{format(utcToZonedTime(course.date, timeZone), "h:mma")}
						&nbsp;
						<Badge bg="secondary" size="sm">{format(utcToZonedTime(course.date, timeZone), "z", { timeZone })}</Badge>
					</p>)}
				title="Time"
			/>

			{course.location && isInPersonEvent(course)
				? (
					<CourseSidebarItem
						Icon={MapIcon}
						description={renderLocation}
						title="Location"
					/>) : null}

			{course.series ? (
				<CourseSidebarItem
					Icon={TicketIcon}
					description={(
						<>
							<p>
								This is part of the <strong>{course.series.title}</strong> Series.
							</p>
							<p className="text-muted">Part of a series - automatically signs you up for all dates in series.</p>
						</>
					)}
					title="Series"
				/>) : null}

			<hr />

			{course.courseInfo.requirements ? (
				<CourseSidebarItem
					Icon={ExclamationCircleIcon}
					description={course.courseInfo.requirements}
					title="Requirements"
				/>) : null}

			{course.courseInfo.requirements ? (
				<CourseSidebarItem
					Icon={PaperClipIcon}
					description={course.courseInfo.prerequisites}
					title="Pre-Requisite"
				/>) : null}

			{course.courseInfo.limitations ? (
				<CourseSidebarItem
					Icon={SupportIcon}
					description={course.courseInfo.limitations}
					title="Limitations"
				/>) : null}

			{course.languages?.length ? (
				<CourseSidebarItem
					Icon={GlobeIcon}
					description={course.languages.join(", ")}
					title="Languages"
				/>) : null}
		</aside>
	);
};

CourseSidebar.propTypes = {
	course: PropTypes.shape({
		courseInfo: PropTypes.shape({
			limitations: PropTypes.string,
			prerequisites: PropTypes.string,
			requirements: PropTypes.string
		}),
		date: PropTypes.instanceOf(Date),
		facilitator: PropTypes.string,
		id: PropTypes.number,
		languages: PropTypes.arrayOf(PropTypes.string),
		location: PropTypes.shape({
			address: PropTypes.shape({
				addressline1: PropTypes.string,
				addressline2: PropTypes.string,
				city: PropTypes.string,
				state: PropTypes.string,
				zip: PropTypes.string
			}),
			name: PropTypes.string
		}),
		series: PropTypes.arrayOf(PropTypes.shape({
			id: PropTypes.number,
			title: PropTypes.string
		}))
	}).isRequired
};

export default CourseSidebar;
